import {
  Badge,
  Box,
  Card,
  CloseButton,
  Icon,
  IconButton,
  Text,
} from '@chakra-ui/react'
import { Handle, NodeProps, Position } from '@xyflow/react'
import React, { ReactNode, useCallback, useMemo } from 'react'

import { ExtensionInstance } from '@mangovoice/types'

import {
  NODE_CARD_OPACITY,
  NODE_CARD_SHADOW,
  Z_INDEX,
} from '../../constants/styling'
import { useSwitchboardT } from '../../types'

/**
 * Shared props for all switchboard nodes
 */
export interface BaseNodeProps extends Partial<NodeProps> {
  /** Badge color palette */
  badgeColor?: string
  /** Node label to display in badge */
  label: string
  /** Content to render in the card body */
  children?: ReactNode
  /** Optional content to render when node is minimized */
  minimizedContent?: ReactNode
  /** Whether to show the "Click to configure" placeholder */
  showPlaceholder?: boolean
  /** Target handle position (default: Left, set to null to hide) */
  targetHandlePosition?: Position | null
  /** Custom style for target handle (for positioning at corners, etc.) */
  targetHandleStyle?: React.CSSProperties
  /** Source handle position (default: Right, set to null to hide) */
  sourceHandlePosition?: Position | null
  /** Label text to display near the target handle */
  targetHandleLabel?: string
  /** Label text to display near the source handle */
  sourceHandleLabel?: string
  /** Additional source handles */
  additionalSourceHandles?: Array<{
    id: string
    position?: Position
    style?: React.CSSProperties
    label?: string
  }>
  /** Compact size for smaller nodes like Start/HangUp */
  compact?: boolean
  /** Callback when delete button is clicked (legacy - use onDeleteNode + id instead) */
  onDelete?: () => void
  /** Callback for node deletion with node ID */
  onDeleteNode?: (nodeId: string) => void
  /** Node ID for delete callback (will use id from NodeProps if not provided) */
  id?: string
  /** Whether delete button should be shown */
  showDeleteButton?: boolean
  /** Whether node is minimized (hides content) */
  minimized?: boolean
  /** Callback when minimize button is clicked */
  onMinimizeToggle?: () => void
  /** Custom border radius for the card (e.g., 'full' for pill shape) */
  borderRadius?: string
  /** Custom horizontal padding for the header */
  headerPaddingX?: number | string
  /** Custom vertical padding for the header */
  headerPaddingY?: number | string
  /** Custom badge background color */
  customBadgeBg?: string
  /** Custom badge text color */
  customBadgeColor?: string
  /** Justify content alignment for header content */
  headerJustifyContent?: 'flex-start' | 'center' | 'flex-end' | 'space-between'
}

/**
 * Base node component with consistent styling
 * All switchboard nodes should use this as their foundation
 */
const BaseNodeComponent: React.FC<BaseNodeProps> = (props) => {
  const {
    badgeColor: _badgeColor, // Unused - colors determined by label using semantic tokens
    label,
    children,
    minimizedContent,
    showPlaceholder = false,
    targetHandlePosition,
    targetHandleStyle,
    sourceHandlePosition,
    targetHandleLabel,
    sourceHandleLabel,
    additionalSourceHandles = [],
    compact = false,
    onDelete,
    onDeleteNode,
    id,
    showDeleteButton = true,
    minimized = false,
    onMinimizeToggle,
    borderRadius: customBorderRadius,
    headerPaddingX: customHeaderPaddingX,
    headerPaddingY: customHeaderPaddingY,
    customBadgeBg,
    customBadgeColor,
    headerJustifyContent: customHeaderJustifyContent,
  } = props
  // Null = hide; undefined = use default (Left/Right)
  const effectiveTargetHandlePosition =
    targetHandlePosition === undefined ? Position.Left : targetHandlePosition
  const effectiveSourceHandlePosition =
    sourceHandlePosition === undefined ? Position.Right : sourceHandlePosition
  // Use semantic tokens: brand for START, red for Hang Up, gray for all others
  // START and Hang Up have white backgrounds with colored text (pill-style)
  const normalizedLabel = label.toUpperCase()
  const isStart = normalizedLabel === 'START'
  const isHangUp = normalizedLabel === 'HANG UP'

  const badgeBg = customBadgeBg ?? 'transparent' // Start and Hang Up use transparent badge on white card
  const badgeColor =
    customBadgeColor ??
    (isStart ? 'brand.solid' : isHangUp ? 'red.solid' : 'fg.default')

  // Don't show delete button for START nodes
  // Support both legacy onDelete and new onDeleteNode + id pattern
  // Extract id and onDeleteNode from props if not explicitly provided
  // (These are injected by helper functions in nodeTypes.tsx)
  const nodeId = id || (props as NodeProps).id
  const deleteNodeHandler =
    onDeleteNode ||
    (props as Partial<NodeProps & { onDeleteNode?: (nodeId: string) => void }>)
      .onDeleteNode
  const hasDeleteHandler = onDelete || (deleteNodeHandler && nodeId)
  const shouldShowDeleteButton =
    showDeleteButton && hasDeleteHandler && !isStart

  // Handle styles - only set size, let CSS handle colors for proper hover/highlight states
  // Colors are defined in react-flow.css using semantic tokens
  const sourceHandleStyle = useMemo(
    () => ({
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      // Colors handled by CSS (.react-flow__handle.source) for hover/highlight support
    }),
    [],
  )

  // Target handle style - only set size, let CSS handle colors
  const targetHandleBaseStyle = useMemo(
    () => ({
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      // Colors handled by CSS (.react-flow__handle.target) for hover/highlight support
    }),
    [],
  )

  // Track hover state for delete button visibility
  const [isHovered, setIsHovered] = React.useState(false)

  // Memoize hover handlers to avoid recreating on every render
  const handleMouseEnter = useCallback(() => setIsHovered(true), [])
  const handleMouseLeave = useCallback(() => setIsHovered(false), [])

  // Use custom borderRadius if provided, otherwise use pill shape for Start and Hang Up nodes
  // Regular card nodes use lg (8px) border radius
  const borderRadius =
    customBorderRadius ?? (isStart || isHangUp ? 'full' : 'lg')

  // Calculate header padding - use custom if provided, otherwise use defaults
  const defaultHeaderPaddingX =
    isStart || isHangUp ? (compact ? 6 : 8) : compact ? 2 : 4
  const defaultHeaderPaddingY =
    isStart || isHangUp ? (compact ? 4 : 5) : compact ? 1 : 2

  const headerPaddingX = customHeaderPaddingX ?? defaultHeaderPaddingX
  const headerPaddingY = customHeaderPaddingY ?? defaultHeaderPaddingY

  // Calculate header justifyContent - use custom if provided, otherwise center for Start/HangUp
  const headerJustifyContent =
    customHeaderJustifyContent ??
    (isStart || isHangUp ? 'center' : 'flex-start')

  const t = useSwitchboardT()
  const placeholderText = t
    ? t('switchboard.nodes.clickToConfigure', {
        defaultMessage: 'Click to configure',
      })
    : 'Click to configure'
  const expandAria = (t && t('switchboard.core.expand')) || 'Expand'
  const minimizeAria = (t && t('switchboard.core.minimize')) || 'Minimize'
  const deleteNodeAria =
    (t && t('switchboard.core.deleteNodeAria')) || 'Delete node'
  const dropLabel = (t && t('switchboard.core.drop')) || 'Drop'
  const dragLabel = (t && t('switchboard.core.drag')) || 'Drag'

  return (
    <Card.Root
      bg="bg.default"
      opacity={NODE_CARD_OPACITY}
      minW={compact ? '120px' : '220px'}
      maxW="280px"
      w="fit-content"
      borderWidth="1px"
      borderColor="border.emphasized"
      shadow={NODE_CARD_SHADOW}
      position="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      borderRadius={borderRadius}
    >
      <Card.Header
        px={headerPaddingX}
        py={headerPaddingY}
        pb={isStart || isHangUp ? undefined : compact ? 1 : 2}
        position="relative"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent={headerJustifyContent}
          gap={0}
          pr={shouldShowDeleteButton ? (compact ? 4 : 6) : 0}
        >
          <Badge
            bg={badgeBg}
            color={badgeColor}
            fontSize="sm"
            fontWeight="bold"
          >
            {normalizedLabel}
          </Badge>
          {onMinimizeToggle && (
            <IconButton
              size="2xs"
              aria-label={minimized ? expandAria : minimizeAria}
              onClick={(e) => {
                e.stopPropagation()
                onMinimizeToggle()
              }}
              variant="ghost"
              minW="auto"
              w="auto"
              h="auto"
              p={0}
            >
              <Icon boxSize={4}>
                {minimized ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m18 15-6-6-6 6" />
                  </svg>
                )}
              </Icon>
            </IconButton>
          )}
        </Box>
        {shouldShowDeleteButton && (
          <CloseButton
            className="delete-button"
            size="2xs"
            aria-label={deleteNodeAria}
            onClick={(e) => {
              e.stopPropagation()
              if (deleteNodeHandler && nodeId) {
                deleteNodeHandler(nodeId)
              } else {
                onDelete?.()
              }
            }}
            colorPalette="neutral"
            bg="transparent"
            position="absolute"
            top={compact ? 1 : 2}
            right={compact ? 1 : 2}
            opacity={isHovered ? 1 : 0}
            visibility={isHovered ? 'visible' : 'hidden'}
            zIndex={Z_INDEX.NODE_DELETE_BUTTON}
            transition="opacity 0.2s, visibility 0.2s"
            _hover={{
              bg: 'transparent',
              color: 'fg.default',
            }}
          />
        )}
      </Card.Header>
      {minimized ? (
        minimizedContent &&
        (!compact ? (
          <Card.Body
            p={4}
            pt={1}
            pb={3}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            {minimizedContent}
          </Card.Body>
        ) : (
          <Card.Body p={2} pb={1} display="flex" flexDirection="column" gap={1}>
            {minimizedContent}
          </Card.Body>
        ))
      ) : !compact ? (
        <Card.Body p={4} pb={4} display="flex" flexDirection="column" gap={2}>
          {children ||
            (showPlaceholder && (
              <Text fontSize="sm" color="fg.muted">
                {placeholderText}
              </Text>
            ))}
        </Card.Body>
      ) : (
        children && (
          <Card.Body p={2} pb={1} display="flex" flexDirection="column" gap={1}>
            {children}
          </Card.Body>
        )
      )}
      {effectiveTargetHandlePosition != null && (
        <>
          <Handle
            type="target"
            position={effectiveTargetHandlePosition as Position}
            isConnectableStart={false}
            style={{
              ...targetHandleBaseStyle,
              ...targetHandleStyle,
            }}
          />
          <Text
            fontSize="xs"
            color="fg.default"
            position="absolute"
            left={
              effectiveTargetHandlePosition === Position.Left ||
              (effectiveTargetHandlePosition === Position.Top &&
                targetHandleStyle?.left === '0%')
                ? '0px'
                : undefined
            }
            right={
              effectiveTargetHandlePosition === Position.Right
                ? '0px'
                : undefined
            }
            top={
              effectiveTargetHandlePosition === Position.Left ||
              effectiveTargetHandlePosition === Position.Right
                ? 'calc(50% - 8px)'
                : effectiveTargetHandlePosition === Position.Top
                  ? '0px'
                  : undefined
            }
            bottom={
              effectiveTargetHandlePosition === Position.Bottom
                ? 'calc(50% - 8px)'
                : undefined
            }
            transform={
              effectiveTargetHandlePosition === Position.Left ||
              (effectiveTargetHandlePosition === Position.Top &&
                targetHandleStyle?.left === '0%')
                ? 'translate(-50%, -100%)'
                : effectiveTargetHandlePosition === Position.Right
                  ? 'translate(50%, -100%)'
                  : 'translateY(-100%)'
            }
            whiteSpace="nowrap"
            pointerEvents="none"
            userSelect="none"
            zIndex={Z_INDEX.NODE_HANDLE_LABEL}
            lineHeight="1.2"
            marginBottom="0"
            paddingBottom="0"
            css={{
              marginBottom: 0,
              paddingBottom: 0,
            }}
          >
            {targetHandleLabel || dropLabel}
          </Text>
        </>
      )}
      {effectiveSourceHandlePosition != null && (
        <>
          <Handle
            type="source"
            position={effectiveSourceHandlePosition as Position}
            isConnectableEnd={false}
            style={sourceHandleStyle}
          />
          <Text
            fontSize="xs"
            color="fg.default"
            position="absolute"
            left={
              effectiveSourceHandlePosition === Position.Left
                ? '0px'
                : undefined
            }
            right={
              effectiveSourceHandlePosition === Position.Right
                ? '0px'
                : undefined
            }
            top={
              effectiveSourceHandlePosition === Position.Left ||
              effectiveSourceHandlePosition === Position.Right
                ? 'calc(50% - 8px)'
                : undefined
            }
            bottom={
              effectiveSourceHandlePosition === Position.Bottom
                ? 'calc(50% - 8px)'
                : undefined
            }
            transform={
              effectiveSourceHandlePosition === Position.Left
                ? 'translate(-50%, -100%)'
                : effectiveSourceHandlePosition === Position.Right
                  ? 'translate(50%, -100%)'
                  : 'translateY(-100%)'
            }
            whiteSpace="nowrap"
            pointerEvents="none"
            userSelect="none"
            zIndex={Z_INDEX.NODE_HANDLE_LABEL}
            lineHeight="1.2"
            marginBottom="0"
            paddingBottom="0"
            css={{
              marginBottom: 0,
              paddingBottom: 0,
            }}
          >
            {sourceHandleLabel || dragLabel}
          </Text>
        </>
      )}
      {additionalSourceHandles.map((handle) => {
        const handlePosition =
          handle.position ||
          (effectiveSourceHandlePosition as Position) ||
          Position.Right

        // Helper function to calculate label position styles for css prop
        const getLabelCssStyles = () => {
          const HANDLE_SIZE = 16
          const LABEL_SPACING = 14 // Space between label and handle

          const cssStyles: Record<string, string | number> = {
            position: 'absolute',
          }

          // Calculate horizontal position and transform
          if (handlePosition === Position.Left) {
            cssStyles.left = '0px'
            cssStyles.transform = `translate(-50%, calc(-100% - ${LABEL_SPACING}px))`
          } else if (handlePosition === Position.Right) {
            cssStyles.right = '0px'
            cssStyles.transform = `translate(50%, calc(-100% - ${LABEL_SPACING}px))`
          } else if (handlePosition === Position.Top) {
            if (handle.style?.left === '0%') {
              cssStyles.left = '0px'
            } else if (handle.style?.left === '100%') {
              cssStyles.left = '100%'
            } else {
              cssStyles.left = '50%'
            }
            cssStyles.top = '0px'
            cssStyles.transform = `translate(-50%, calc(-100% - ${LABEL_SPACING}px))`
          } else if (handlePosition === Position.Bottom) {
            if (handle.style?.right !== undefined) {
              cssStyles.right = '0px'
              cssStyles.transform = `translate(50%, calc(-100% - ${LABEL_SPACING}px))`
            } else {
              cssStyles.left = '50%'
              cssStyles.transform = `translate(-50%, calc(-100% - ${LABEL_SPACING}px))`
            }
            cssStyles.bottom = `${HANDLE_SIZE}px`
          }

          // Calculate vertical position for Left/Right handles
          // Position label at handle center (handle top + 8px), transform will position it above
          if (
            handlePosition === Position.Left ||
            handlePosition === Position.Right
          ) {
            if (handle.style?.top) {
              // Handle top edge is at handle.style.top, center is 8px below
              // Position label top edge at handle center, transform shifts it up
              const topValue = handle.style.top
              if (typeof topValue === 'string' && topValue.includes('calc')) {
                // Extract the calc expression and add 8px to position at handle center
                // Use string manipulation instead of regex to avoid polynomial complexity
                const calcStart = topValue.indexOf('calc(')
                if (calcStart !== -1) {
                  // Find matching closing parenthesis by counting depth
                  let depth = 0
                  let calcEnd = calcStart + 5 // Start after 'calc('
                  for (let i = calcStart + 5; i < topValue.length; i++) {
                    if (topValue[i] === '(') depth++
                    else if (topValue[i] === ')') {
                      if (depth === 0) {
                        calcEnd = i
                        break
                      }
                      depth--
                    }
                  }
                  if (calcEnd > calcStart + 5) {
                    const expr = topValue.substring(calcStart + 5, calcEnd)
                    cssStyles.top = `calc(${expr} + 8px)`
                  } else {
                    cssStyles.top = topValue
                  }
                } else {
                  cssStyles.top = topValue
                }
              } else if (
                typeof topValue === 'string' &&
                topValue.includes('px')
              ) {
                const num = parseFloat(topValue)
                cssStyles.top = `${num + 10}px`
              } else {
                cssStyles.top = topValue
              }
            } else {
              cssStyles.top = 'calc(50% - 8px)'
            }
          }

          return cssStyles
        }

        return (
          <React.Fragment key={handle.id}>
            <Handle
              type="source"
              position={handlePosition}
              id={handle.id}
              isConnectableEnd={false}
              style={{
                ...sourceHandleStyle,
                ...handle.style,
              }}
            />
            {handle.label && (
              <Text
                fontSize="xs"
                color="fg.default"
                whiteSpace="nowrap"
                pointerEvents="none"
                userSelect="none"
                zIndex={Z_INDEX.NODE_HANDLE_LABEL}
                lineHeight="1.2"
                marginBottom="0"
                paddingBottom="0"
                css={{
                  marginBottom: 0,
                  paddingBottom: 0,
                  ...getLabelCssStyles(),
                }}
              >
                {handle.label}
              </Text>
            )}
          </React.Fragment>
        )
      })}
    </Card.Root>
  )
}

/**
 * Memoized BaseNode to prevent unnecessary re-renders during drag operations.
 * Only re-renders when relevant props change (data, label, etc.), not on position changes.
 * Ignores React Flow internal props like selected, dragging, zIndex, position that change during drag.
 *
 * Note: Children comparison is intentionally lenient - we allow children to change reference
 * because parent components may recreate JSX. The actual content stability is ensured by
 * memoizing the individual node components that render the children.
 */
export const BaseNode = React.memo<BaseNodeProps>(
  BaseNodeComponent,
  (prevProps, nextProps) => {
    // Re-render if id changes
    const prevId = prevProps.id || (prevProps as NodeProps).id
    const nextId = nextProps.id || (nextProps as NodeProps).id
    if (prevId !== nextId) return false

    // Re-render if label changes
    if (prevProps.label !== nextProps.label) return false

    // Re-render if minimized state changes
    if (prevProps.minimized !== nextProps.minimized) return false

    // Re-render if children change (reference comparison)
    // Note: Individual node components are memoized, so if they don't re-render,
    // children will be stable. If children reference changes, it's likely meaningful.
    if (prevProps.children !== nextProps.children) return false
    if (prevProps.minimizedContent !== nextProps.minimizedContent) return false

    // Re-render if additional source handles change (reference comparison is fine here)
    if (prevProps.additionalSourceHandles !== nextProps.additionalSourceHandles)
      return false

    // Re-render if handle positions change
    if (
      prevProps.targetHandlePosition !== nextProps.targetHandlePosition ||
      prevProps.sourceHandlePosition !== nextProps.sourceHandlePosition
    )
      return false

    // Re-render if handle labels change
    if (
      prevProps.targetHandleLabel !== nextProps.targetHandleLabel ||
      prevProps.sourceHandleLabel !== nextProps.sourceHandleLabel
    )
      return false

    // Re-render if showDeleteButton changes
    if (prevProps.showDeleteButton !== nextProps.showDeleteButton) return false

    // Re-render if compact changes
    if (prevProps.compact !== nextProps.compact) return false

    // Data comparison - React Flow may create new data objects with same content during drag
    // Use shallow reference check first (fast path), then deep comparison if needed
    const prevData = prevProps.data
    const nextData = nextProps.data

    // Fast path: if same reference, data hasn't changed
    if (prevData === nextData) {
      // Same reference - skip further checks
    } else if (!prevData && !nextData) {
      // Both null/undefined - same
    } else if (!prevData || !nextData) {
      // One is null/undefined and other isn't - data changed
      return false
    } else if (typeof prevData === 'object' && typeof nextData === 'object') {
      // Both are objects - need deep comparison
      // Compare keys and values efficiently
      const prevKeys = Object.keys(prevData).sort()
      const nextKeys = Object.keys(nextData).sort()

      // If key count differs, data changed
      if (prevKeys.length !== nextKeys.length) return false

      // Check if all keys match
      if (prevKeys.some((key, i) => key !== nextKeys[i])) return false

      // Check if all values match (shallow comparison)
      for (const key of prevKeys) {
        if (prevData[key] !== nextData[key]) {
          // Values differ - data changed
          return false
        }
      }
      // Data references differ but content is the same - skip re-render
    } else {
      // Different types or primitives - data changed
      return false
    }

    // Children comparison: Allow children reference to change during drag
    // The individual node components are memoized, so if they don't re-render,
    // their children content will be stable. If children reference changes but
    // parent didn't re-render meaningfully, it's likely just React Flow passing
    // new references. We trust the parent memoization to prevent unnecessary changes.
    // NOTE: This means children changes during normal (non-drag) operations will
    // still trigger re-renders as expected, which is the desired behavior.

    // Skip re-render if only React Flow internal props changed:
    // - position: handled by React Flow internally during drag
    // - selected: changes during drag but doesn't require re-render
    // - dragging: changes during drag but doesn't require re-render
    // - zIndex: changes during drag but doesn't require re-render
    // - width/height: dimensions change but don't require re-render
    // - data reference: checked above, only re-render if content changed
    // - children reference: allowed to change during drag (parent memoization handles stability)
    // All other React Flow internal props are ignored
    return true
  },
)

/**
 * Format extension ID to display name
 * @param extensionId - Extension ID (string or number)
 * @param extensionMap - Map of extension ID to ExtensionInstance
 * @returns Formatted display string or the original ID if not found
 */
export const formatExtensionDisplay = (
  extensionId: string | number | undefined,
  extensionMap: Map<number, ExtensionInstance>,
): string | null => {
  if (!extensionId) return null

  const extId =
    typeof extensionId === 'string' ? parseInt(extensionId, 10) : extensionId
  if (isNaN(extId)) return String(extensionId)

  const ext = extensionMap.get(extId)
  if (!ext) return String(extensionId)

  return ext.name ? `${ext.number} - ${ext.name}` : ext.number || String(extId)
}

/**
 * Helper function for React.memo comparison that compares node props efficiently.
 * Ignores React Flow internal props that change during drag (position, selected, dragging, etc.)
 * and does deep comparison of data to handle new references with same content.
 *
 * Use this for individual node components to prevent unnecessary re-renders during drag.
 */
export function nodePropsAreEqual<T extends NodeProps>(
  prevProps: T,
  nextProps: T,
): boolean {
  // Re-render if id changes
  if (prevProps.id !== nextProps.id) return false

  // Deep compare data - React Flow may create new data objects with same content
  const prevData = prevProps.data
  const nextData = nextProps.data

  if (prevData !== nextData) {
    if (!prevData && !nextData) {
      // Both null/undefined - same
    } else if (!prevData || !nextData) {
      return false // One null, other isn't - changed
    } else if (typeof prevData === 'object' && typeof nextData === 'object') {
      const prevKeys = Object.keys(prevData).sort()
      const nextKeys = Object.keys(nextData).sort()
      if (prevKeys.length !== nextKeys.length) return false
      if (prevKeys.some((key, i) => key !== nextKeys[i])) return false
      if (prevKeys.some((key) => prevData[key] !== nextData[key])) return false
      // Same content - skip re-render
    } else {
      return false
    }
  }

  // Re-render if showDeleteButton changes (if both components use it)
  if ('showDeleteButton' in prevProps && 'showDeleteButton' in nextProps) {
    if (prevProps.showDeleteButton !== nextProps.showDeleteButton) return false
  }

  // Ignore React Flow internal props (selected, dragging, position, etc.)
  return true
}

/**
 * Format a key to a human-readable label
 * @param key - Field key (e.g., "extensionId", "maxWaitTime")
 * @returns Formatted label (e.g., "Extension Id", "Max Wait Time")
 */
export const formatFieldLabel = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/_/g, ' ')
}
